<template>
  <div class="container">
    <p>
      {{ t('determineCO2FlowRate') }}
      <strong>{{ t('co2SafetyNote') }}</strong>
    </p>
    <EquationBlock
      :equations="equations"
    />
    <hr />
    <div class="row">
      <div class="col-sm">
        <GenericInput
          :input-label="t('roomWidth')"
          v-model.number="growSpaceWidthFt"
          :conversion-factors="conversionFactors.basicDistance"
        />
        <GenericInput
          :input-label="t('roomLength')"
          v-model.number="growSpaceLengthFt"
          :conversion-factors="conversionFactors.basicDistance"
        />
        <GenericInput
          :input-label="t('roomHeight')"
          v-model.number="growSpaceHeightFt"
          :conversion-factors="conversionFactors.basicDistance"
        />
        <GenericInput
          :input-label="t('volumeCompensation')"
          v-model.number="volumeReductionFt3"
          :conversion-factors="conversionFactors.basicVolume"
        />
        <FixedUnitInput
          :input-label="t('ambientCO2')"
          v-model.number="ambientPPM"
          :unit="t('ppm')"
        />
        <FixedUnitInput
          :input-label="t('desiredCO2')"
          v-model.number="desiredPPM"
          :unit="t('ppm')"
        />
        <FixedUnitInput
          :input-label="t('minutesOn')"
          v-model.number="minutesOn"
          :unit="t('minutes')"
        />
      </div>
      <div class="col-sm">
        <FixedUnitOutput
          :output-label="t('roomVolume')"
          :number="roomVolume"
          :unit="t('ftCubed')"
        />
        <FixedUnitOutput
          :output-label="t('desiredAdditionalCO2')"
          :number="addtlPPM"
          :unit="t('ppm')"
        />
        <FixedUnitOutput
          :output-label="t('fixedAdditionalCO2')"
          :number="ft3HrFlowRate"
          :unit="t('ftCubedPerHour')"
        />
        <FixedUnitOutput
          :output-label="t('co2FlowRatePerHour')"
          :number="timedFtHrFlowRate"
          :unit="t('scfh')"
        />
        <FixedUnitOutput
          :output-label="t('co2FlowRatePerMinute')"
          :number="timedFtHrFlowRatePerMinute"
          :unit="t('scfm')"
        />
      </div>
    </div>
    <i>{{ t('Co2RoomVolumeNote') }}</i>
  </div>
</template>

  
  <script>
  import { ref, computed, reactive } from 'vue';
  import GenericInput from '../inputs/GenericInput.vue';
  import FixedUnitInput from '../inputs/FixedUnitInput.vue';
  import FixedUnitOutput from '../inputs/FixedUnitOutput.vue';
  import EquationBlock from '../inputs/EquationBlock.vue';
  import ConversionFactors from '../../utils/conversion_factors';
  import { defaultRound, round } from '../../utils/math';
  import { useI18n } from 'vue-i18n';
  
  export default {
    components: {
      GenericInput,
      FixedUnitInput,
      FixedUnitOutput,
      EquationBlock,
    },
    setup() {
      const growSpaceWidthFt = ref(10);
      const growSpaceLengthFt = ref(10);
      const growSpaceHeightFt = ref(8);
      const volumeReductionFt3 = ref(0);
      const ambientPPM = ref(400);
      const desiredPPM = ref(1500);
      const minutesOn = ref(15);

      const { t, tm } = useI18n();

      // Create a reactive object with a function
      const translations = reactive({
        t: (key) => t(key)
      })

      const equations = computed(() => {
          return tm('co2CalcEquations');
      })
  
      const roomVolume = computed(() => {
        return (
          growSpaceWidthFt.value *
          growSpaceLengthFt.value *
          growSpaceHeightFt.value -
          volumeReductionFt3.value
        );
      });
  
      const addtlPPM = computed(() => {
        return desiredPPM.value - ambientPPM.value;
      });
  
      const ft3HrFlowRate = computed(() => {
        return round(addtlPPM.value * 1e-6 * roomVolume.value,2);
      });
  
      const timedFtHrFlowRate = computed(() => {
        return defaultRound(ft3HrFlowRate.value / (minutesOn.value / 60));
      });

      const timedFtHrFlowRatePerMinute = computed(()=>{
        return defaultRound(timedFtHrFlowRate.value/60);
      })
  
      return {
        growSpaceWidthFt,
        growSpaceLengthFt,
        growSpaceHeightFt,
        volumeReductionFt3,
        ambientPPM,
        desiredPPM,
        minutesOn,
        roomVolume,
        addtlPPM,
        ft3HrFlowRate,
        timedFtHrFlowRate,
        conversionFactors: ConversionFactors,
        equations,
        timedFtHrFlowRatePerMinute,
        t: translations.t
      };
    },
  };
  </script>
  